import { Injectable } from '@angular/core';

interface EventType<T> {
  readonly name: string;
  dummy?: T;  // just to make TypeScript happy, since we don't need the T here actually
};

/**Declare one event type in a type-safe way
usage:
```
export const myEvent = declareEvent<{ value1: number, value2: string}>('myEvent');
```
*/
export function declareEvent<T>(name: string): EventType<T> {
  return { name };
}

type PubSubCallback<T> = (event: T) => void;

// Navigation events
export const navigationStartingEvent = declareEvent<void>('navigationStarting');
export const navigationStackChangedEvent = declareEvent<{ canGoBack: boolean; pagesLength: number }>('navigationStackChanged');

// Coaching events
export const coachBannerEvent = declareEvent<{ clientDisplayName: string } | null>('coachBanner');

// Training page events
export const startWorkoutFromNavEvent = declareEvent<void>('startWorkoutFromNav');
export const workoutStartableStateChangedEvent = declareEvent<{ canStart: boolean }>('workoutStartableStateChanged');

// Consent events
// opens the cookie/consent preferences dialog on demand (Settings "Privacy
// Choices" row, #4219) so a user can review or change their consent answers
export const openConsentPreferencesEvent = declareEvent<void>('openConsentPreferences');
// this device's Advertising answer changed (#5587). A checkout that already
// built its tracking data rebuilds it, so the answer just given is the one sent.
export const adConsentChangedEvent = declareEvent<void>('adConsentChanged');
// open the Terms / Privacy consent screen for review after the text changed
// (#5587). Published by the home dashboard's "privacy policy updated" card; the
// app-level consent dialog answers by opening its consent screen in a form the
// person can close again.
export const openTermsReviewEvent = declareEvent<void>('openTermsReview');
// the signed-in account's consent records were just saved and the cached login
// result now holds the new ones (#5587). The home dashboard's "privacy policy
// updated" card re-asks the consent-refresh rule when this arrives, so the card
// goes away as soon as the person accepts — the consent screen is an overlay and
// does not re-create the dashboard, so nothing else would tell it.
export const accountConsentsChangedEvent = declareEvent<void>('accountConsentsChanged');

export interface PubSubRegistration {
  unsubscribe(): void;
}

class PubSubRegistrationImpl<T> implements PubSubRegistration {
  constructor(public pubSubService: PubSubService, public event: EventType<T>, public callback: PubSubCallback<T>) { }

  unsubscribe(): void {
    this.pubSubService.unsubscribe_internal(this);
  }
}

@Injectable()
export class PubSubService {
  eventRegistrations: { [eventName: string]: PubSubRegistrationImpl<any>[] } = {};

  constructor() { }

  /// Subscribe to events of particular type. To unsubscribe, call `unsubscribe()` on the returned object.
  subscribe<T>(event: EventType<T>, callback: PubSubCallback<T>): PubSubRegistration {
    const eventName = event.name;
    if (!this.eventRegistrations[eventName]) {
      this.eventRegistrations[eventName] = [];
    }
    let reg: PubSubRegistrationImpl<T> = new PubSubRegistrationImpl(this, event, callback);
    this.eventRegistrations[eventName].push(reg);
    return reg as PubSubRegistration;
  }

  /// Publish an event of particular type.
  publish<T>(event: EventType<T>, payload: T) {
    let handlers = this.eventRegistrations[event.name];
    if (handlers) {
      handlers.forEach(h => h.callback(payload));
    }
  }

  // INTERNAL. Use reg.ubsubscribe() instead of this method
  unsubscribe_internal<T>(reg: PubSubRegistrationImpl<T>) {
    const eventName = reg.event.name;
    if (this.eventRegistrations[eventName]) {
      this.eventRegistrations[eventName] = this.eventRegistrations[eventName].filter(r => r !== reg);
    }
  }
}
